import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "../components/ui/table";
import { Users, ClipboardList, Send, FileCheck2, Bell, Mail, ListChecks } from "lucide-react";
import { toast } from "sonner";

const PORTAL = ["invited", "active", "submitted", "under_review", "overdue"];
const PORTAL_TONE = { invited: "warning", active: "lime", submitted: "success", under_review: "warning", overdue: "danger" };

function Stat({ label, value, unit, sub, icon: Icon, testid, tone }) {
  return (
    <div className="card-flat p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2"><Icon className="h-4 w-4" /><span className="label-eyebrow text-[10px]">{label}</span></div>
      <div className={`font-mono text-2xl font-bold tracking-tight ${tone || ""}`}>{value}<span className="text-xs text-muted-foreground font-sans ml-1">{unit}</span></div>
      {sub && <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );
}

export default function SupplierPortalAdmin() {
  const [d, setD] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [subs, setSubs] = useState([]);

  const load = useCallback(() => {
    api.get("/supplier-portal-admin/dashboard").then((r) => setD(r.data)).catch(() => setD({ empty: true }));
    api.get("/supplier-portal-admin/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
    api.get("/supplier-portal-admin/templates").then((r) => setTemplates(r.data)).catch(() => {});
    api.get("/supplier-portal-admin/submissions").then((r) => setSubs(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => { try { await api.post(`/supplier-portal-admin/suppliers/${id}/portal-status`, { status }); load(); toast.success("Portal status updated"); } catch { toast.error("Failed"); } };
  const remind = async (id) => { try { await api.post(`/supplier-portal-admin/suppliers/${id}/remind`); toast.success("Reminder sent (SIMULATED)"); } catch { toast.error("Failed"); } };

  if (!d) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading portal admin…</div>;
  if (d.empty) return <div className="p-8"><div className="card-flat p-12 text-center"><h2 className="font-display text-2xl font-bold">No suppliers yet.</h2></div></div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="supplier-portal-admin-page">
      <div>
        <div className="label-eyebrow mb-1">Supply Chain · Supplier Portal Admin</div>
        <h1 className="font-display text-4xl font-bold tracking-tighter flex items-center gap-3"><Users className="h-8 w-8 text-accent" /> Supplier Portal Admin</h1>
        <p className="text-muted-foreground text-sm mt-1 max-w-2xl">Manage supplier portal access, questionnaire templates, data submissions and engagement reminders.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <Stat label="Suppliers" value={d.total_suppliers} unit="" icon={Users} testid="spa-stat-suppliers" />
        <Stat label="Avg completion" value={d.avg_completion} unit="%" icon={ListChecks} testid="spa-stat-completion" />
        <Stat label="Profile submitted" value={d.portal.submitted} unit="" icon={FileCheck2} testid="spa-stat-submitted" tone="text-[#00E65B]" />
        <Stat label="Under review" value={d.portal.under_review} unit="" icon={ClipboardList} testid="spa-stat-under-review" tone="text-[#FFB800]" />
        <Stat label="Reminders due" value={d.reminders_due} unit="" icon={Bell} testid="spa-stat-reminders" tone={d.reminders_due ? "text-red-500" : ""} />
        <Stat label="Templates" value={d.templates} unit="" icon={ClipboardList} testid="spa-stat-templates" />
      </div>
      <p className="text-[11px] text-muted-foreground -mt-2" data-testid="spa-submitted-note">
        "Profile submitted" means the supplier completed their portal questionnaire/profile. This is a different stage from a formal ESG disclosure — submitting a profile auto-creates a Draft disclosure in the Disclosure Lifecycle for your GHG lead to review and formally submit.
      </p>

      <Tabs defaultValue="access">
        <TabsList className="flex flex-wrap h-auto">
          <TabsTrigger value="access" data-testid="spa-tab-access">Suppliers & Access</TabsTrigger>
          <TabsTrigger value="templates" data-testid="spa-tab-templates">Questionnaire Templates</TabsTrigger>
          <TabsTrigger value="submissions" data-testid="spa-tab-submissions">Submissions & Reminders</TabsTrigger>
        </TabsList>

        <TabsContent value="access" className="mt-4">
          <div className="card-flat overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Supplier</TableHead><TableHead>ESG contact</TableHead><TableHead>Country</TableHead>
                <TableHead className="text-right">Completion</TableHead><TableHead>Portal access</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {suppliers.map((s) => (
                  <TableRow key={s.id} data-testid={`spa-supplier-row-${s.id}`}>
                    <TableCell className="text-sm font-medium">{s.name}</TableCell>
                    <TableCell className="text-xs font-mono text-muted-foreground">{s.esg_contact || "—"}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{s.country}</TableCell>
                    <TableCell className="text-right"><div className="flex items-center gap-2 justify-end"><span className="font-mono text-xs">{s.questionnaire_completion}%</span><Progress value={s.questionnaire_completion} className="h-1 w-16" /></div></TableCell>
                    <TableCell>
                      <Select value={s.portal_status} onValueChange={(v) => setStatus(s.id, v)}>
                        <SelectTrigger className="h-7 w-[130px] text-xs" data-testid={`spa-status-${s.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{PORTAL.map((p) => <SelectItem key={p} value={p}>{p.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell><Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => remind(s.id)} data-testid={`spa-remind-${s.id}`}><Bell className="h-3 w-3" /> Remind</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="templates" className="mt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {templates.map((t) => (
              <div key={t.id} className="card-flat p-4" data-testid={`spa-template-${t.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div><div className="text-sm font-medium">{t.name}</div><div className="text-[11px] text-muted-foreground">{t.framework}</div></div>
                  <Badge variant="lime" className="text-[9px]">{t.questions} questions</Badge>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(t.sections || []).map((s) => <Badge key={s.name} variant="outline" className="text-[10px]">{s.name}</Badge>)}
                </div>
              </div>
            ))}
            {!templates.length && <div className="text-sm text-muted-foreground p-4">No templates yet.</div>}
          </div>
        </TabsContent>

        <TabsContent value="submissions" className="mt-4">
          <div className="card-flat overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Supplier</TableHead><TableHead>Period</TableHead><TableHead>Due</TableHead><TableHead>Response</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {subs.map((r) => (
                  <TableRow key={r.id} data-testid={`spa-submission-row-${r.id}`}>
                    <TableCell className="text-sm font-medium">{r.supplier_name || "—"}</TableCell>
                    <TableCell className="font-mono text-xs">{r.period}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{r.due_date ? new Date(r.due_date).toLocaleDateString() : "—"}</TableCell>
                    <TableCell>{r.responded ? <Mail className="h-4 w-4 text-[#00E65B]" /> : <Send className="h-4 w-4 text-muted-foreground" />}</TableCell>
                    <TableCell><Badge variant={r.status === "confirmed" || r.status === "responded" ? "success" : "warning"} className="text-[9px]">{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {!subs.length && <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground text-sm">No submissions yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
